/**
 * Le contrat de la **recherche de transactions**, côté navigateur (`B-11`,
 * `EX-BO-004`, `EX-LDG-002`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `TransactionsMessages.TransactionPageView`,
 * sans rien y ajouter. Un contrôle confronte champ par champ cette déclaration
 * au **fichier Java lui-même** : recopier un contrat, c'est en faire une
 * seconde source de vérité qui vieillira (`D-35`, `D-45`).
 *
 * ## Ce qui manquait, et ce n'était pas un écran
 *
 * Le module « Transactions » était **déclaré** : une route `/transactions` dans
 * la table des modules, une icône, une entrée de menu, un module `TRANSACTIONS`
 * dans `admin.bo_action`, et le droit `TRANSACTION_VIEW` accordé depuis la V7 à
 * six rôles sur sept. Et **aucune route serveur ne servait de transaction au
 * back-office** : rien sous `/api/backoffice/transactions`, à aucune couche.
 *
 * C'est la onzième occurrence de la famille de `D-25`, `D-30`, `D-35`, `D-45`
 * et `D-57`, prise par l'autre bout. Les dix précédentes étaient des routes
 * servies, éprouvées, vertes, qu'aucun écran n'appelait. Celle-ci est un
 * **droit accordé que rien ne sert** : six rôles portaient une habilitation qui
 * ne pouvait s'exercer nulle part.
 *
 * ## Ce que le serveur rend et que la maquette ne dessine pas
 *
 * Le dessin ignore six colonnes que `transactions.transaction` porte, et
 * chacune vaut pour un contrôleur financier :
 *
 *  - **`reversal_of_id`, dans les deux sens** — un réversal désigne ce qu'il
 *    annule, et une opération contre-passée sait par quoi. Le dessin parle de
 *    réversal dans sa note et dans l'une de ses commandes, et ne montre jamais
 *    le lien ;
 *  - **`plan_version_id`** — la version du plan tarifaire qui a coté
 *    l'opération (epic 10). C'est ce qui permet de dire *pourquoi ces frais-là*,
 *    et la colonne est protégée en écriture par un déclencheur : elle ne ment
 *    pas ;
 *  - **`idempotency_key`** — deux appels servis par la même clé sont *le même*
 *    paiement ; distinguer cela d'un double paiement est exactement le travail
 *    d'un contrôleur, et l'index de cette colonne n'est délibérément pas
 *    unique ;
 *  - **`channel` et `initiated_by_type`** — d'où vient l'opération, et qui l'a
 *    lancée ;
 *  - **`total_debited`**, servi à côté de `amount` et `feeAmount` pour que
 *    l'écran puisse les **confronter** ;
 *  - **`failure_code` et `narrative`**.
 *
 * Et deux choses que la base porte et que ni le dessin ni les colonnes de la
 * transaction ne savent dire :
 *
 *  - **les écritures en partie double** (`ledger.ledger_entry`), que le panneau
 *    du dessin montre sans que rien ne les serve. Leur somme algébrique est ce
 *    qui prouve qu'une opération est équilibrée, et c'est le seul endroit du
 *    produit où un contrôleur peut le vérifier lui-même ;
 *  - **`ledgerPosted`** — cette opération a-t-elle produit des écritures ? Une
 *    opération échouée n'en produit aucune, et une case vide se lirait comme
 *    une donnée perdue.
 *
 * ## L'identité EST servie, et la trace le dit
 *
 * Le premier livré rendait deux numéros de compte et refusait de résoudre les
 * noms, au motif qu'une liste qui révèle cinquante états civils sous une entrée
 * d'audit muette est une fuite tue. **Le porteur a tranché autrement**, et il a
 * raison sur le besoin : un contrôleur qui lit
 * `CLI8917011472272 → CLI8917055293393` ne contrôle rien.
 *
 * L'argument, lui, reste vrai — il change de remède. Le serveur résout
 * l'identité **et inscrit au journal combien d'identités il a révélées**. La
 * règle tenue est celle de la file de revue KYC, passée de `KYC_QUEUE` à
 * `KYC_QUEUE_IDENTITIES` : on ne refuse pas de montrer, on refuse de montrer
 * *sans le dire*.
 *
 * ## Trois routes, et la troisième est une porte
 *
 *  - `GET /api/backoffice/transactions` **liste** ;
 *  - `GET /api/backoffice/transactions/{id}` rend **une** opération, et c'est
 *    une seconde lecture avec sa propre trace : dépiler la ligne déjà reçue
 *    aurait affiché une fiche que le journal ne mentionne pas ;
 *  - `GET /api/backoffice/parties/{accountId}` rend la fiche d'un **acteur** —
 *    son solde, son identité, ses statistiques, et son dossier d'identité *si
 *    le rôle a le droit de le voir*. Cette dernière condition est décidée par le
 *    serveur et **dite** (`kycVisible`) : masquer côté écran n'aurait rien valu,
 *    c'est ce qu'`EX-BO-002` refuse.
 *
 * Les opérations d'un acteur passent par la **première** route, avec
 * `accountId` : une route jumelle aurait eu ses propres filtres, sa propre
 * pagination et sa propre trace, et les deux auraient divergé au premier
 * ajustement.
 *
 * ## Ce qui est traduit, et ce qui ne l'est pas
 *
 * `txType`, `status`, `channel`, `failureCode`, `entryType` restent des
 * **codes**. L'écran leur donne un libellé français **avec repli sur le code
 * lui-même** : c'est une couche d'affichage, et non la liste blanche partagée
 * que `G3` refuse — il n'existe nulle part de liste centrale des types
 * d'opération, ils viennent de paramètres système, et un type ajouté par
 * configuration s'affiche alors tel quel, jamais masqué.
 */

import type { ApiClient } from './api-client.js';
import { ApiError } from './api-client.js';
// La route unique des actions du socle et la forme de son accusé vivent déjà
// dans le module du rapprochement, qui les a écrits pour `PERIOD_CLOSE`. Les
// réécrire ici en ferait deux déclarations d'un seul contrat, et le jour de la
// divergence c'est l'un des deux écrans qui lirait un accusé qu'il n'a pas reçu.
import { backOfficeActionPath } from './reconciliation.js';
import type { ActionAcknowledgement } from './reconciliation.js';

/** Le chemin de la recherche, tel que `TransactionsApi` le sert. */
export const transactionsPath = '/api/backoffice/transactions';

/**
 * Le code du réversal manuel dans la matrice — `MANUAL_REVERSAL`, en ✔✔.
 *
 * **C'est la commande « Écriture de réversal » du dessin, et elle est servie.**
 * Elle ne passe pas par une route de l'écran : l'écriture unique du socle
 * (`EX-BO-002`) met la demande en attente et rend `202` avec l'identifiant de
 * la demande. La seconde signature se donne dans la file de double validation
 * du tableau de bord, et nulle part ailleurs — c'est ce qui rend vraie la note
 * du dessin : « un réversal ne modifie jamais une écriture : il en crée une
 * inverse, sous double validation (EX-LDG-002) ».
 *
 * La matrice ne l'accorde qu'à **la finance**, et en double validation. Les
 * cinq autres rôles qui peuvent consulter les transactions ne peuvent pas la
 * demander ; le serveur refuse, et l'écran rend le refus tel quel.
 */
export const manualReversalAction = 'MANUAL_REVERSAL';

/**
 * Le motif d'audit de la recherche — une **constante**, et non une saisie.
 *
 * C'est la conduite du listage du journal d'audit et des quatre lectures du
 * rapprochement, et la raison est la même : le contrôleur ouvre cet écran
 * toujours pour la même raison — contrôler les opérations. Une invite par
 * ouverture coûterait une frappe pour une phrase identique, et l'écran ne
 * pourrait plus s'ouvrir sur la journée, ce que la maquette montre.
 *
 * Le motif du **réversal**, lui, est écrit à la main : c'est une décision, et
 * il rejoint les deux identités dans le journal. Les confondre inscrirait
 * « contrôle des opérations » comme motif d'une contre-écriture.
 */
export const transactionsAuditReason = 'controle-des-operations';

/**
 * Une écriture du grand livre, exactement telle que le serveur la rend.
 *
 * `direction` vaut `D` ou `C` — le caractère que `ledger_entry.direction`
 * stocke. `amount` est **toujours positif** : c'est le sens, et lui seul, qui
 * porte le signe.
 *
 * `balanceAfter` peut manquer, et ce n'est pas zéro : les comptes de
 * cantonnement, de revenus et de commissions ne tiennent pas de solde
 * (`ledger.account.maintains_balance`). Nul veut dire « ce compte ne tient pas
 * de solde », et l'afficher « 0 F » se lirait comme un compte vidé.
 */
export interface LedgerEntry {
  readonly direction: string;
  readonly amount: number;
  readonly entryType: string;
  readonly balanceAfter: number | null;
  readonly bookedAt: string;
  readonly accountNumber: string;
  readonly accountType: string;
  readonly ownerType: string;
  readonly currency: string;
}

/**
 * Une **contrepartie** : le compte, et qui le possède.
 *
 * Le premier livré ne rendait qu'un numéro de compte, et l'écran refusait de
 * résoudre l'identité. Le porteur a tranché — « vaut mieux afficher numéro de
 * compte, numéro de téléphone et nom complet » — et il a raison sur le besoin :
 * un contrôleur qui lit `CLI8917011472272 → CLI8917055293393` ne contrôle rien.
 *
 * **Ce que cela coûte est payé côté serveur, et non tu** : la route inscrit au
 * journal d'audit combien d'identités elle a révélées. La règle tenue est celle
 * de la file de revue KYC, passée de `KYC_QUEUE` à `KYC_QUEUE_IDENTITIES` — on
 * ne refuse pas de montrer, on refuse de montrer *sans le dire*.
 *
 * Quatre champs peuvent manquer, et chacun pour une raison qui se lit : un
 * compte **interne** — cantonnement, revenus, commissions — n'a ni client ni
 * guichet ; un compte de guichet n'a pas de numéro de téléphone ; et un client
 * dont la pièce n'a pas encore été déposée n'a **pas de nom enregistré**, ce qui
 * est exactement le cas qui a coûté `D-95`.
 */
export interface Party {
  readonly accountId: string;
  readonly accountNumber: string;
  readonly accountType: string;
  readonly ownerType: string;
  readonly customerId: string | null;
  readonly customerRef: string | null;
  readonly msisdn: string | null;
  readonly displayName: string | null;
  readonly outletCode: string | null;
  readonly outletName: string | null;
}

/**
 * Une opération, exactement telle que le serveur la rend.
 *
 * `statusTone` est **servi** : ce ne sont pas trois couleurs inventées par
 * l'écran, ce sont les trois constantes que `TransactionStatus` déclare —
 * réussie, en cours, échouée — et le serveur les connaît déjà puisqu'il compte
 * avec. Les deviner ici aurait recréé la liste blanche partagée de `G3`, et
 * peint en vert un état que le serveur n'appelle pas un succès. Un état que le
 * domaine n'a pas encore nommé est rendu `OTHER`, donc **neutre** : le peindre
 * en rouge affirmerait un échec que personne n'a constaté.
 *
 * **Douze champs peuvent manquer**, et chacun pour une raison qui se lit :
 *
 *  - `currency`, `debit` et `credit` sont absents d'une opération qui n'a
 *    produit aucune écriture et dont les deux colonnes dénormalisées sont
 *    vides — une opération échouée avant tout mouvement. La devise vit sur
 *    `ledger.account` et nulle part ailleurs : sans compte, il n'y a pas de
 *    devise, et en supposer une afficherait un montant faux comme un fait ;
 *  - `narrative` est absent d'une opération sans libellé ;
 *  - `failureCode` est absent d'une opération qui n'a pas échoué ;
 *  - `completedAt` est absent tant que l'opération n'est pas dénouée ;
 *  - `reversesId` / `reversesRef` sont absents de ce qui ne contre-passe rien,
 *    `reversedById` / `reversedByRef` de ce qui n'a pas été contre-passé ;
 *  - `planCode`, `planVersionNo` et `planStatus` sont absents quand **aucune
 *    version n'a coté** l'opération. C'est une information, pas une lacune : un
 *    réversal n'est pas tarifé.
 */
export interface Transaction {
  readonly id: string;
  readonly publicRef: string;
  readonly txType: string;
  readonly status: string;
  readonly statusTone: string;
  readonly amount: number;
  readonly feeAmount: number;
  readonly totalDebited: number;
  readonly currency: string | null;
  readonly debit: Party | null;
  readonly credit: Party | null;
  readonly ledgerPosted: boolean;
  readonly channel: string;
  readonly initiatedByType: string;
  readonly idempotencyKey: string;
  readonly narrative: string | null;
  readonly failureCode: string | null;
  readonly initiatedAt: string;
  readonly completedAt: string | null;
  readonly reversesId: string | null;
  readonly reversesRef: string | null;
  readonly reversedById: string | null;
  readonly reversedByRef: string | null;
  readonly planCode: string | null;
  readonly planVersionNo: number | null;
  readonly planStatus: string | null;
  readonly entries: readonly LedgerEntry[];
}

/** Une valeur présente dans la fenêtre, et **combien de fois**. */
export interface Facet {
  readonly code: string;
  readonly count: number;
}

/** Un état présent dans la fenêtre, sa nature servie, et combien de fois. */
export interface StatusFacet {
  readonly code: string;
  readonly tone: string;
  readonly count: number;
}

/**
 * Une **page** de la recherche, exactement telle que le serveur la rend.
 *
 * `total` est ce que la fenêtre et les filtres retiennent **en tout**, jamais
 * la taille de la page : la maquette écrit « 6 résultats sur 1 372 transactions
 * du jour », et un compte qui vaudrait la taille de la page serait un chiffre
 * faux affiché comme un fait.
 *
 * **Aucun indicateur n'est calculé par le navigateur.** Le porteur l'a demandé
 * en toutes lettres — « tout doit venir du serveur, aucun calcul côté front » —
 * et la raison n'est pas l'élégance : un chiffre calculé dans le navigateur est
 * un chiffre que le journal d'audit ne peut pas justifier. `total`, `volume`,
 * `fees`, `succeeded`, `inFlight`, `failed` et `successRate` sortent tous du
 * même `SELECT`, sur la même fenêtre et les mêmes filtres — donc ils se
 * **recalculent avec les filtres**.
 *
 * `successRate` est `null` sur une fenêtre vide, et c'est la seule chose qui
 * compte à son sujet : `0 / 0` n'est pas « aucun succès », c'est « aucune
 * opération ». Un taux affiché à zéro sur une journée sans activité se lirait
 * comme une panne générale du système de paiement.
 *
 * `from` et `to` sont les bornes **effectivement appliquées**, y compris quand
 * l'écran n'en a demandé aucune. `types` et `statuses` sont les valeurs
 * **réellement présentes** dans la fenêtre, avec leur compte — calculées sur la
 * fenêtre seule, sans les autres filtres, pour qu'une liste ne devienne pas
 * vide au premier choix.
 */
export interface TransactionPage {
  readonly transactions: readonly Transaction[];
  readonly total: number;
  readonly volume: number;
  readonly fees: number;
  readonly succeeded: number;
  readonly inFlight: number;
  readonly failed: number;
  readonly successRate: number | null;
  readonly page: number;
  readonly pageSize: number;
  readonly from: string;
  readonly to: string;
  readonly types: readonly Facet[];
  readonly statuses: readonly StatusFacet[];
}

/**
 * Le compte du grand livre d'un acteur.
 *
 * `balance` et `availableBalance` manquent sur un compte qui ne tient pas de
 * solde — `maintains_balance` vaut faux pour le cantonnement, les revenus et
 * les commissions. Nul veut dire « ce compte ne tient pas de solde », et
 * l'afficher « 0 F » se lirait comme un compte vidé.
 */
export interface PartyAccount {
  readonly accountId: string;
  readonly accountNumber: string;
  readonly accountType: string;
  readonly ownerType: string;
  readonly currency: string;
  readonly balance: number | null;
  readonly availableBalance: number | null;
  readonly status: string;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly frozenAt: string | null;
  readonly freezeReason: string | null;
}

/**
 * Le client propriétaire du compte.
 *
 * `displayName` manque quand le client n'a pas encore déposé sa pièce : il n'a
 * alors **pas de nom enregistré**, et c'est exactement la population du pilote
 * — le cas qui a coûté `D-95` sur le guichet.
 */
export interface PartyCustomer {
  readonly customerId: string;
  readonly publicRef: string;
  readonly msisdn: string;
  readonly displayName: string | null;
  readonly kycLevel: number | null;
  readonly status: string;
  readonly screeningStatus: string;
  readonly pep: boolean | null;
  readonly lastActivityAt: string | null;
}

/** Le guichet propriétaire du compte. */
export interface PartyOutlet {
  readonly outletId: string;
  readonly outletCode: string;
  readonly businessName: string;
  readonly category: string;
  readonly region: string | null;
  readonly status: string;
}

/**
 * Ce qu'un acteur a fait, **sur toute son histoire** et calculé en SQL.
 *
 * Sans fenêtre, et c'est délibéré : la fiche d'un acteur répond à « qui
 * est-ce », pas à « qu'a-t-il fait ce jour-là ». `debited` et `credited`
 * distinguent un compte qui envoie d'un compte qui reçoit, et c'est la première
 * question qu'un contrôleur pose devant un compte qu'il ne connaît pas.
 */
export interface PartyStats {
  readonly operations: number;
  readonly volume: number;
  readonly fees: number;
  readonly succeeded: number;
  readonly inFlight: number;
  readonly failed: number;
  readonly successRate: number | null;
  readonly debited: number;
  readonly credited: number;
  readonly firstAt: string | null;
  readonly lastAt: string | null;
}

/** Le dossier d'identité le plus récent du client, quand le rôle a le droit de le voir. */
export interface KycCase {
  readonly caseId: string;
  readonly targetLevel: number;
  readonly status: string;
  readonly channel: string;
  readonly submittedAt: string;
  readonly decidedAt: string | null;
  readonly decisionReason: string | null;
  readonly autoApproved: boolean;
  readonly gestureVerified: boolean | null;
}

/**
 * La fiche d'un **acteur**, exactement telle que le serveur la rend.
 *
 * **`kycVisible` n'est pas une commodité, c'est le cœur de cet écran.**
 * `KYC_REVIEW_QUEUE_VIEW` n'est accordé qu'à trois rôles sur sept —
 * `COMPLIANCE`, `SUPPORT_N2`, `AUDITOR`. La finance peut voir les opérations et
 * **pas** les dossiers d'identité, et c'est une séparation des tâches, pas un
 * oubli.
 *
 * Le drapeau est servi **à part** du dossier, et c'est tout son intérêt :
 * `kyc === null` avec `kycVisible === false` veut dire « vous n'avez pas le
 * droit de voir », et `kyc === null` avec `kycVisible === true` veut dire « ce
 * client n'a aucun dossier ». Les deux se rendraient d'une case vide identique,
 * et ce sont deux faits opposés — c'est la distinction que le tableau de bord
 * fait déjà entre « aucune mesure » et « zéro ».
 *
 * `customer` et `outlet` sont exclusifs, et tous deux peuvent manquer : un
 * compte interne n'a pas de propriétaire nommé.
 */
export interface PartyProfile {
  readonly account: PartyAccount;
  readonly customer: PartyCustomer | null;
  readonly outlet: PartyOutlet | null;
  readonly stats: PartyStats;
  readonly kycVisible: boolean;
  readonly kyc: KycCase | null;
}

/**
 * Ce qu'une recherche retient. **Aucun champ n'est obligatoire**, et c'est tout
 * l'objet de cette route : le dessin montre une page qui s'ouvre déjà remplie,
 * indicateurs compris.
 */
export interface TransactionQuery {
  /** Journée de début, en ISO `AAAA-MM-JJ`. Absente : les sept derniers jours. */
  readonly from?: string;
  /** Journée de fin, incluse. Absente : la journée courante. */
  readonly to?: string;
  /** Le type d'opération filtré. Absent : tous. */
  readonly type?: string;
  /** L'état filtré. Absent : tous. */
  readonly status?: string;
  /** Le montant plancher, en unités mineures. Absent : aucun plancher. */
  readonly minAmount?: string;
  /**
   * Le compte d'un acteur — ses opérations, qu'il soit débité ou crédité.
   *
   * C'est ce qui permet à la fiche d'un acteur de lister ses opérations **sans
   * seconde route**, avec la même pagination, les mêmes filtres et la même
   * trace que l'écran de recherche.
   */
  readonly accountId?: string;
  /** Référence, clé d'idempotence, numéro de compte, numéro, nom, montant ou libellé. */
  readonly search?: string;
  /** La page demandée, à partir de 1. Absente : la première. */
  readonly page?: number;
}

/** Le chemin d'une opération désignée. */
export function transactionPath(transactionId: string): string {
  return `${transactionsPath}/${encodeURIComponent(transactionId)}`;
}

/** Le chemin de la fiche d'un acteur, tel que `TransactionsApi` le sert. */
export function partyPath(accountId: string): string {
  return `/api/backoffice/parties/${encodeURIComponent(accountId)}`;
}

/**
 * Cherche des transactions.
 *
 * Aucune capture d'erreur : l'`ApiError` du client porte déjà sa nature et sa
 * clé de message. L'avaler pour rendre une page vide ferait confondre « aucune
 * opération dans cette fenêtre » — un fait — avec « le serveur a refusé », qui
 * n'en est pas un. Sur l'écran d'un contrôleur financier, la confusion se lit
 * comme une journée sans activité, c'est-à-dire l'inverse de ce qui s'est
 * passé.
 */
export async function searchTransactions(
  client: ApiClient,
  query: TransactionQuery = {},
  reason: string = transactionsAuditReason,
): Promise<TransactionPage> {
  const parametres = new URLSearchParams({ reason });
  // Un filtre vide n'est PAS envoyé vide : `type=` serait un critère que le
  // serveur devrait interpréter, là où son absence dit sans ambiguïté « tous ».
  if (query.from !== undefined && query.from !== '') parametres.set('from', query.from);
  if (query.to !== undefined && query.to !== '') parametres.set('to', query.to);
  if (query.type !== undefined && query.type !== '') parametres.set('type', query.type);
  if (query.status !== undefined && query.status !== '') parametres.set('status', query.status);
  if (query.minAmount !== undefined && query.minAmount !== '') {
    parametres.set('minAmount', query.minAmount);
  }
  if (query.accountId !== undefined && query.accountId !== '') {
    parametres.set('accountId', query.accountId);
  }
  if (query.search !== undefined && query.search !== '') parametres.set('search', query.search);
  if (query.page !== undefined) parametres.set('page', String(query.page));
  const page = await client.request<unknown>(`${transactionsPath}?${parametres.toString()}`);
  if (!estUnePage(page)) throw malForme(client);
  return page;
}

/**
 * Lit **une** opération.
 *
 * C'est une seconde lecture, et elle laisse sa propre trace : le serveur
 * inscrit au journal la référence de l'opération, le nombre d'écritures et le
 * nombre d'identités révélées. Dépiler la ligne déjà reçue aurait affiché une
 * fiche que le journal ne mentionne pas — la promesse affichée et non tenue que
 * le module Clients a déjà refusée.
 */
export async function fetchTransaction(
  client: ApiClient,
  transactionId: string,
  reason: string = transactionsAuditReason,
): Promise<Transaction> {
  const recue = await client.request<unknown>(
    `${transactionPath(transactionId)}?reason=${encodeURIComponent(reason)}`,
  );
  if (!estUneOperation(recue)) throw malForme(client);
  return recue;
}

/**
 * Lit la fiche d'un **acteur**.
 *
 * Aucune capture d'erreur : rendre une fiche vide ferait confondre « ce compte
 * n'existe pas » avec « le serveur a refusé ». Sur l'écran où l'on regarde le
 * solde d'un client, la confusion se lit comme un compte à zéro.
 */
export async function fetchParty(
  client: ApiClient,
  accountId: string,
  reason: string = transactionsAuditReason,
): Promise<PartyProfile> {
  const recue = await client.request<unknown>(
    `${partyPath(accountId)}?reason=${encodeURIComponent(reason)}`,
  );
  if (!estUnActeur(recue)) throw malForme(client);
  return recue;
}

/**
 * **Demande** une contre-écriture. Elle ne la prononce pas.
 *
 * `MANUAL_REVERSAL` est en ✔✔ dans la matrice, et pour la seule finance : le
 * socle met la demande en attente et rend `202` avec l'identifiant de la
 * demande. La seconde signature se donne dans la file de double validation déjà
 * livrée — celle du tableau de bord —, et nulle part ailleurs. C'est le point
 * entier d'`EX-LDG-002` : un réversal qu'un seul agent pourrait prononcer
 * serait de l'argent rendu sans second regard.
 *
 * Le motif est **obligatoire** côté serveur (`requireReason`) ; il l'est donc
 * aussi côté écran, sans quoi l'agent lirait « le serveur a rencontré une
 * erreur » là où il manque une phrase de sa main.
 *
 * `entityRef` désigne ce sur quoi on agit — c'est ce que le journal d'audit
 * inscrit ; `payload.transactionId` est ce que `ManualReversalExecutor` lit.
 * Les deux portent la même opération, et ce n'est pas une redondance : les lire
 * au même endroit ferait une entrée d'audit qui ne désigne pas l'objet, ou une
 * contre-écriture qui annule on ne sait quoi.
 */
export async function requestManualReversal(
  client: ApiClient,
  transactionId: string,
  reason: string,
): Promise<ActionAcknowledgement> {
  const recue = await client.request<unknown>(backOfficeActionPath(manualReversalAction), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entityRef: transactionId,
      reason,
      payload: { transactionId },
    }),
  });
  if (!estUnAccuse(recue)) throw malForme(client);
  return recue;
}

/**
 * Le refus typé d'une réponse qui n'a pas la forme attendue.
 *
 * **Ce qui revient du réseau est vérifié avant d'être cru.** Une réponse à
 * moitié formée ferait lever l'écran au rendu — une page blanche au lieu d'un
 * registre — ou, pire sur une liste, se lirait comme « aucune opération ».
 * Rendre une erreur typée fait dire « le serveur a répondu autre chose », ce
 * qui est exactement le cas.
 */
function malForme(client: ApiClient): ApiError {
  return new ApiError({
    kind: 'server',
    status: null,
    correlationId: client.lastCorrelationId ?? '',
  });
}

function estUnObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

function estUnePage(valeur: unknown): valeur is TransactionPage {
  if (!estUnObjet(valeur)) return false;
  return (
    Array.isArray(valeur.transactions) &&
    typeof valeur.total === 'number' &&
    typeof valeur.volume === 'number' &&
    typeof valeur.succeeded === 'number' &&
    typeof valeur.inFlight === 'number' &&
    typeof valeur.pageSize === 'number' &&
    typeof valeur.from === 'string' &&
    typeof valeur.to === 'string' &&
    Array.isArray(valeur.types) &&
    Array.isArray(valeur.statuses)
  );
}

function estUneOperation(valeur: unknown): valeur is Transaction {
  return (
    estUnObjet(valeur) &&
    typeof valeur.publicRef === 'string' &&
    typeof valeur.statusTone === 'string' &&
    Array.isArray(valeur.entries)
  );
}

/**
 * Une fiche d'acteur, vérifiée avant d'être crue.
 *
 * `kycVisible` est exigé **explicitement** : c'est un booléen, et un booléen
 * absent se lit `undefined`, donc faux. Un écran qui déduirait « pas le droit de
 * voir » d'une réponse à moitié formée dirait un refus que personne n'a
 * prononcé, ce qui est exactement l'inverse de ce que ce champ existe pour
 * porter.
 */
function estUnActeur(valeur: unknown): valeur is PartyProfile {
  if (!estUnObjet(valeur)) return false;
  return (
    estUnObjet(valeur.account) &&
    estUnObjet(valeur.stats) &&
    typeof valeur.kycVisible === 'boolean'
  );
}

function estUnAccuse(valeur: unknown): valeur is ActionAcknowledgement {
  return estUnObjet(valeur) && typeof valeur.outcome === 'string';
}
